/**
 * QR scanning — native scanner on iOS/Android, `getUserMedia` + html5-qrcode on web.
 *
 * WHY THIS EXISTS (Card 20, task C). QRPage drove the scanner with
 * `navigator.mediaDevices.getUserMedia()`. On iOS that had never worked at all: until
 * task A the app shipped no `NSCameraUsageDescription`, so the first touch of the camera
 * terminated the process. With the string in place `getUserMedia` *can* work inside
 * WKWebView, but it remains the fragile path — camera enumeration, autoplay and track
 * lifetime all behave differently there than in Safari, and the failure mode is a blank
 * rectangle rather than an error anyone can act on.
 *
 * A native scanner is both the reliable answer and a real native capability for the
 * Guideline 4.2 argument: a full-screen system camera surface the app owns, not a
 * `<video>` element in a web page.
 *
 * PLUGIN CHOICE — and the one the card suggested, rejected.
 * The card proposed `@capacitor-mlkit/barcode-scanning` "or equivalent", with the Card 1
 * rule attached: a Capacitor plugin without a `Package.swift` is silently dropped from
 * this SPM iOS build and fails at runtime as "feature unavailable", with no build error.
 * `@capacitor-mlkit/barcode-scanning@8.1.0` ships `CapacitorMlkitBarcodeScanning.podspec`
 * and NO `Package.swift` — it would have been dropped. `@capacitor/barcode-scanner`
 * (Ionic's own, v3.1.0, peer `@capacitor/core >=8`) ships one, and `npx cap update ios`
 * confirms it: "All Capacitor plugins have a Package.swift file". It also presents its
 * own full-screen native UI, so none of the transparent-WebView overlay work an in-page
 * ML Kit preview needs applies here.
 *
 * WEB IS UNCHANGED. Browsers keep the existing in-page html5-qrcode scanner in QRPage —
 * it works, and the plugin's own web implementation is the same library with less
 * control over the surrounding UI.
 */

import { isNative } from '@/utils/platform';

/**
 * Why a scan produced no value. `cancelled` is ordinary (the user hit the close button)
 * and must stay silent; `denied` needs saying out loud, because neither platform will
 * re-prompt for a camera permission the user has already refused.
 */
export type ScanFailureReason = 'cancelled' | 'denied' | 'failed';

export class ScanError extends Error {
  readonly reason: ScanFailureReason;

  constructor(reason: ScanFailureReason, message: string) {
    super(message);
    this.name = 'ScanError';
    this.reason = reason;
  }
}

/** Whether the native scanner is available. `false` on web — callers keep their own. */
export function isNativeScannerAvailable(): boolean {
  return isNative;
}

/**
 * The plugin surfaces failures as `OS-PLUG-BARC-NNNN` codes on the rejected error
 * (OSBarcodeError.swift / OSBarcodePlugin.kt), which is far steadier ground than the
 * human-readable message — the messages are localised and have changed between
 * releases. The message check is only a fallback for a build that stops sending codes.
 */
const CANCELLED_CODE = 'OS-PLUG-BARC-0006';
const CAMERA_DENIED_CODE = 'OS-PLUG-BARC-0007';

function classifyScanError(error: unknown): ScanFailureReason {
  const code = String((error as { code?: unknown })?.code ?? '');
  if (code === CANCELLED_CODE) return 'cancelled';
  if (code === CAMERA_DENIED_CODE) return 'denied';

  const message = (error instanceof Error ? error.message : String(error ?? '')).toLowerCase();
  if (message.includes('cancel')) return 'cancelled';
  if (message.includes('camera access') || message.includes('permission') || message.includes('denied')) {
    return 'denied';
  }
  return 'failed';
}

/**
 * Open the native full-screen QR scanner.
 *
 * Resolves with the decoded text, or `null` when the user cancels. Throws `ScanError`
 * for a denied camera and for genuine scan failures.
 *
 * Throws if called on web — callers must gate on `isNativeScannerAvailable()`.
 */
export async function scanQrCode(scanInstructions?: string): Promise<string | null> {
  if (!isNative) {
    throw new ScanError('failed', 'The native scanner is only available in the mobile app.');
  }

  // Lazy import so the plugin (and the html5-qrcode copy its web implementation pulls
  // in) never enters the web bundle's critical path.
  const { CapacitorBarcodeScanner, CapacitorBarcodeScannerTypeHint } = await import(
    '@capacitor/barcode-scanner'
  );

  try {
    const result = await CapacitorBarcodeScanner.scanBarcode({
      // QR only. `ALL` would happily decode the barcode on a box of screws and hand us a
      // string that fails URL parsing a moment later with a confusing message.
      hint: CapacitorBarcodeScannerTypeHint.QR_CODE,
      scanInstructions: scanInstructions ?? 'Point your camera at a Trade Loop QR code',
      // Continuous scanning — no shutter button to press.
      scanButton: false,
      cancelButtonAccessibilityLabel: 'Close scanner',
      torchButtonOnAccessibilityLabel: 'Turn flashlight off',
      torchButtonOffAccessibilityLabel: 'Turn flashlight on',
    });

    return result.ScanResult || null;
  } catch (error) {
    const reason = classifyScanError(error);
    if (reason === 'cancelled') return null;
    if (reason === 'denied') {
      throw new ScanError(
        'denied',
        'Trade Loop does not have permission to use the camera. Turn it on in your device Settings under Trade Loop, then try again.',
      );
    }
    throw new ScanError('failed', 'The scanner could not start. Please try again.');
  }
}
